/**
 * bso **ads** — domain layer (the ONLY app-specific part).
 *
 * The rest of the kit (`types`/`gate`/`caps`/`config`/`provider`/`placements`/
 * `rewards`) is generic and is copied as-is from the canonical implementation (`listto`); this file
 * is the one each app rewrites with its row in the "plan por app" table.
 *
 * bso row (`infra/guides/ADS_STRATEGY.md` §10, row 3): tier **A** with an
 * asterisk — «A suave». And **the asterisk overrides the tier**:
 *
 *  1. **NEVER an interstitial in the reading or study flow.** Interrupting
 *     someone reading Scripture or viewing a lexicon entry
 *     with a full-screen ad is not a capping violation: it is
 *     a betrayal of the product. Tier A would allow it; the domain policy does not.
 *  2. **Contextual ads, NEVER behavioral ads based on religious activity.** What
 *     the user reads, highlights, searches, or writes is special-category data (GDPR
 *     art. 9). It does not segment ads, leave this module, or feed profiles. It is
 *     implemented in code (`contextual-provider.ts`), not by a promise.
 *  3. **No banner.** The tier would allow it; in a reading app, a
 *     permanent fixed strip is exactly the kind of presence that the
 *     respectful-tone policy rejects. A test asserts that it remains empty.
 *
 * The three rules live in [`assertBsoAdPolicy`], which scans the ENTIRE
 * registry: a placement that fails the policy **breaks the tests**. It is not a
 * convention written in a guide nobody rereads.
 */

import { DEFAULT_AD_CAP_POLICY, strictestAdCapPolicy } from "./gate";
import { definePlacements } from "./placements";
import type { AdRewardsStore } from "./rewards";
import type { AdCapPolicy, AdKind, AdTier, PlacementRegistry } from "./types";

/** bso ad tier (§9): consumption — but see the asterisk in the header. */
export const BSO_AD_TIER: AdTier = "A";

// ─────────────────────────────────────────────────────────────────────────────
// Superficies
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Classification of bso screens **from the point of view of the
 * ad policy**. It is not the app's route taxonomy: it is the only
 * question that matters here — *what is the user doing on this screen?*
 */
export const BSO_AD_SURFACES = ["reading", "study", "catalog", "done", "reward"] as const;

export type BsoAdSurface = (typeof BSO_AD_SURFACES)[number];

/** Surfaces where NO ad may appear, in any format. */
export const BSO_FORBIDDEN_SURFACES: readonly BsoAdSurface[] = ["reading", "study"];

/**
 * Route prefixes that are **reading or study**. They are checked by PREFIX
 * deliberately: a future study screen is covered without anyone having
 * to remember adding it to a list (a test asserts this with a route
 * inventada).
 *
 * Real routes carry the account prefix `/u/<id>`, which
 * [`isReadingOrStudyRoute`] normalizes before comparing.
 */
export const BSO_READING_ROUTE_PREFIXES = ["/bible", "/dictionary/", "/strong"] as const;

/** Removes the account prefix `/u/<id>` so prefixes can be compared. */
export function stripAccountPrefix(pathname: string): string {
  const match = /^\/u\/[^/]+(?<rest>\/.*)?$/.exec(pathname);
  return match?.groups?.rest ?? (/^\/u\/[^/]+$/.test(pathname) ? "/" : pathname);
}

/**
 * Is the user reading or studying? If yes, **there is no
 * ad**, whatever the capping schedule says.
 */
export function isReadingOrStudyRoute(pathname: string): boolean {
  const path = stripAccountPrefix(pathname);
  return BSO_READING_ROUTE_PREFIXES.some(
    (p) => path === p || path.startsWith(p.endsWith("/") ? p : `${p}/`),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Recompensas
// ─────────────────────────────────────────────────────────────────────────────

/** bso reward types (stable IDs: persisted in local storage). */
export const REWARD_STREAK_FREEZE = "streak-freeze";

// ─────────────────────────────────────────────────────────────────────────────
// Registro de placements
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Registry of bso placements. It is the source of truth: a breakpoint not
 * listed here cannot render an ad.
 *
 * **No banner and only ONE interstitial**, and both gaps are deliberate
 * (see the header and §"what is NOT here" below).
 */
export const BSO_AD_PLACEMENTS = definePlacements({
  /**
   * Day of a reading plan marked complete. It is the app's ONLY respite from
   * the app: the user has closed a unit of value and is in transit. It is not
   * the reader — it is the plan screen, with the checkbox already marked.
   */
  "plan-day-done": { kind: "interstitial", breakpoint: "done-screen" },
  /** Strong dictionary catalog: long list, navigation-oriented surface. */
  "dictionary-catalog": { kind: "native", breakpoint: "feed-slot" },
  /** Search results: end of search, long list. */
  "search-results": { kind: "native", breakpoint: "feed-slot" },
  /**
   * "Salva tu racha": the opt-in rewarded placement alongside the existing economy.
   * The freezes feed the gamification engine's `computeStreak`, which in
   * bso derives the streak from the user's own annotations.
   */
  "streak-save": {
    kind: "rewarded",
    breakpoint: "reward-moment",
    reward: { type: REWARD_STREAK_FREEZE, amount: 1 },
  },
} as const);

export type BsoPlacementId = keyof typeof BSO_AD_PLACEMENTS;

/**
 * Surface for each placement. Declaring it is MANDATORY: a placement without
 * a surface makes [`assertBsoAdPolicy`] fail (fail-closed — whoever adds a
 * placement must say where it lives, and that is where it is asked whether it is
 * lectura o estudio).
 */
export const BSO_PLACEMENT_SURFACES: Readonly<Record<BsoPlacementId, BsoAdSurface>> = {
  "plan-day-done": "done",
  "dictionary-catalog": "catalog",
  "search-results": "catalog",
  "streak-save": "reward",
};

/**
 * Formats bso supports. **`banner` is NOT included**: tier A would allow it, but the
 * domain policy does not (a permanent fixed strip in a devotional reading app
 * devocional).
 */
export const BSO_ALLOWED_KINDS: readonly AdKind[] = ["interstitial", "native", "rewarded"];

// ─────────────────────────────────────────────────────────────────────────────
// Capping: stricter than the portfolio default
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Numeric policy for bso. It tightens the §4 default on the three levers that
 * affect interruption, and it can **only tighten**: the gate applies the
 * minimum of this policy and its own caps.
 *
 * - `interstitialPerDay: 1` (portfolio: 3). There is only one disruptive placement
 *   and it only fires when closing a plan day: more than one per day
 *   would mean the user completed two plan days, and then
 *   they deserve less advertising, not more.
 * - `minGapBetweenInterstitialsMs`: 30 min (portfolio: 10).
 * - `interruptionBudgetMs`: 30 min (portfolio: 10).
 */
export const BSO_AD_CAP_POLICY: Partial<AdCapPolicy> = {
  interstitialPerSession: 1,
  interstitialPerDay: 1,
  minGapBetweenInterstitialsMs: 30 * 60 * 1000,
  interruptionBudgetMs: 30 * 60 * 1000,
  allowInterstitialOnFirstSession: false,
  minUnitsCompleted: 1,
};

/**
 * **The only correct way to construct bso's capping policy.**
 *
 * Composes `DEFAULT_AD_CAP_POLICY` + [`BSO_AD_CAP_POLICY`] + whatever comes from
 * configuration, **always taking the strictest value** ([`strictestAdCapPolicy`]).
 *
 * ## Why this function exists instead of an object
 *
 * Having the gate perform the strict merge protects its own baseline, but **does not protect
 * against a caller that flattens the domain policy BEFORE calling**: a
 * `{ ...BSO_AD_CAP_POLICY, ...backOfficeConfig }` arrives at gate already
 * relaxed, and there is nothing left to save there. Observed: the gate test passed
 * with the strict merge and still allowed the ad because the spread had
 * ocurrido en el call site.
 *
 * That is why `ads-context` builds the policy ONLY here, just as
 * `makeBsoAdProvider` is the only provider constructor: if there is a single
 * entry point, there is no way to bypass it from a screen.
 *
 * @param overrides operational configuration (back office, env, tier). It can only
 *                  TIGHTEN; a looser value is ignored.
 */
export function bsoAdCapPolicy(overrides?: Partial<AdCapPolicy>): AdCapPolicy {
  return strictestAdCapPolicy(DEFAULT_AD_CAP_POLICY, BSO_AD_CAP_POLICY, overrides);
}

// ─────────────────────────────────────────────────────────────────────────────
// Policy guard — the one that turns the three rules into a TEST
// ─────────────────────────────────────────────────────────────────────────────

export interface BsoAdPolicyViolation {
  placementId: string;
  reason:
    | "surface-not-declared"
    | "ad-on-reading-or-study-surface"
    | "interstitial-outside-done-screen"
    | "banner-forbidden"
    | "kind-not-allowed"
    | "rewarded-outside-reward-surface";
  detail: string;
}

/**
 * Scans the ENTIRE registry and returns all violations of bso's soft
 * policy (empty = all good). All violations are returned at once instead of
 * failing on the first: whoever adds a placement wants to see the full list.
 *
 * It complements the kit's `validatePlacements` —which checks the generic
 * legality of the **tier**— and is strictly more restrictive.
 */
export function validateBsoAdPolicy(
  registry: PlacementRegistry = BSO_AD_PLACEMENTS,
  surfaces: Readonly<Record<string, BsoAdSurface>> = BSO_PLACEMENT_SURFACES,
): BsoAdPolicyViolation[] {
  const violations: BsoAdPolicyViolation[] = [];

  for (const [placementId, def] of Object.entries(registry)) {
    const surface = surfaces[placementId];

    if (surface === undefined) {
      violations.push({
        placementId,
        reason: "surface-not-declared",
        detail:
          "todo placement debe declarar su superficie en BSO_PLACEMENT_SURFACES " +
          "(es donde se responde si esa pantalla es lectura o estudio)",
      });
      // Without a surface, the rest cannot be judged: fail closed and continue to the next.
      continue;
    }

    if (BSO_FORBIDDEN_SURFACES.includes(surface)) {
      violations.push({
        placementId,
        reason: "ad-on-reading-or-study-surface",
        detail: `la superficie '${surface}' es lectura/estudio: ahí no cabe NINGÚN anuncio`,
      });
    }

    if (!BSO_ALLOWED_KINDS.includes(def.kind)) {
      violations.push({
        placementId,
        reason: def.kind === "banner" ? "banner-forbidden" : "kind-not-allowed",
        detail: `el formato '${def.kind}' no está admitido en bso`,
      });
    }

    if (def.kind === "interstitial" && (surface !== "done" || def.breakpoint !== "done-screen")) {
      violations.push({
        placementId,
        reason: "interstitial-outside-done-screen",
        detail:
          "un interstitial solo puede vivir en una pantalla de 'hecho' " +
          `(superficie 'done' + breakpoint 'done-screen'); aquí es '${surface}'/'${def.breakpoint}'`,
      });
    }

    if (def.kind === "rewarded" && surface !== "reward") {
      violations.push({
        placementId,
        reason: "rewarded-outside-reward-surface",
        detail: `un rewarded es una oferta opt-in junto a la economía de la app, no en '${surface}'`,
      });
    }
  }

  return violations;
}

/** Test/bootstrap sugar: throws with the details if there are violations. */
export function assertBsoAdPolicy(
  registry: PlacementRegistry = BSO_AD_PLACEMENTS,
  surfaces: Readonly<Record<string, BsoAdSurface>> = BSO_PLACEMENT_SURFACES,
): void {
  const violations = validateBsoAdPolicy(registry, surfaces);
  if (violations.length > 0) {
    const detail = violations.map((v) => `  · ${v.placementId}: ${v.detail}`).join("\n");
    throw new Error(`bso ad policy violated:\n${detail}`);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Wiring into the economy that ALREADY exists
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Streak freezes earned through rewarded ads. They are the `freezes` consumed by
 * the gamification engine's `computeStreak`. They are read from the local-first store, so
 * they **work for guests** and without network access.
 *
 * It is safe for them to live on the client: in bso the streak is **DERIVED** from the
 * `created_at` of synced annotations (there is no server-side inventory that a client
 * could mint), so a local freeze cannot inflate anything the server considers
 * authoritative. This is the §5 criterion:
 * *Can the server re-derive the value?* Yes, here.
 */
export function adFreezes(store: AdRewardsStore, now: number = Date.now()): number {
  return store.amountOf(REWARD_STREAK_FREEZE, now);
}

/**
 * ── What is NOT in the registry, and why ────────────────────────────────
 *
 * The §10 table lists two rewards for bso: «desbloqueo temporal de
 * diccionario/recurso extra 48 h» and «tema de lectura premium». Neither is
 * declared, **and that is a decision, not an oversight**:
 *
 * In bso today the Strong lexicon, concordance, full dictionary,
 * all 11 locales, all fonts, and **the entire theme catalog are
 * free**. There is nothing locked to unlock. A 48-hour `resource-unlock` for
 * content that is already free is a disguised stub, and §5 of the
 * strategy requires reusing value that ALREADY exists (precedent recorded in §12
 * a partir de kid-games).
 *
 * And the alternative —blocking themes or dictionaries so they can be «regalarlos»— is
 * worse than doing nothing: this app publishes an **in-place update** to
 * users from 2022. Taking away something they already have to sell it back is the
 * peor jugada posible del lanzamiento.
 *
 * What DOES have real value and is therefore present: the streak freeze, which feeds
 * the badges users pursue (`devoted-reader` 7 d, `faithful-month`
 * 30 d).
 *
 * When genuinely premium content exists, plugging it in means **declaring the
 * placement**: the rest of the machinery (gate, type-based capping, offer backoff,
 * temporary redemption) is already proven.
 */
